import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../../components/DirectionalIcon';
import { colors, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { AssignmentAgentBreakdown } from '../../services/api';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface AssignmentBreakdownCardProps {
  byAgent: AssignmentAgentBreakdown[];
  onViewAll?: () => void;
}

/** Per-agent assignment counts — a ranked list rather than a chart, since a
 * typical business has a handful of agents (a bar/donut would be visual
 * overhead for 2-5 data points); each row's own bar fill communicates
 * relative share without needing a separate chart component. */
export const AssignmentBreakdownCard: React.FC<AssignmentBreakdownCardProps> = ({ byAgent, onViewAll }) => {
  const styles = useThemedStyles(getStyles);
  const maxTotal = Math.max(...byAgent.map((a) => a.total), 1);

  return (
    <View style={styles.card}>
      <Text style={styles.title}>Assignments by agent</Text>
      {byAgent.length === 0 ? (
        <View style={styles.empty}>
          <Ionicons name="people-outline" size={22} color={colors.text.secondary as string} />
          <Text style={styles.emptyText}>No contacts assigned yet</Text>
        </View>
      ) : (
        <View style={styles.list}>
          {byAgent.map((agent) => (
            <View key={agent.userId} style={styles.row}>
              <View style={styles.rowHeader}>
                <Text style={styles.name} numberOfLines={1}>{agent.name}</Text>
                <Text style={styles.total}>{agent.total}</Text>
              </View>
              <View style={styles.barTrack}>
                <View style={[styles.barFill, { width: `${(agent.total / maxTotal) * 100}%` }]} />
              </View>
              <Text style={styles.subtext}>
                {agent.active} active · {agent.completed} completed
                {agent.overdue > 0 ? ` · ${agent.overdue} overdue` : ''}
              </Text>
            </View>
          ))}
        </View>
      )}

      {onViewAll && byAgent.length > 0 && (
        <TouchableOpacity style={styles.viewAll} onPress={onViewAll} activeOpacity={0.7}>
          <Text style={styles.viewAllText}>View all</Text>
          <DirectionalIcon name="chevron-forward" size={14} color={colors.brand.primary as string} />
        </TouchableOpacity>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: designTokens.spacing.md,
    ...designTokens.shadows.sm,
  },
  title: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    marginBottom: designTokens.spacing.sm,
  },
  list: {
    gap: designTokens.spacing.sm,
  },
  row: {
    gap: 2,
  },
  rowHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  name: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  total: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
  },
  barTrack: {
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.background.secondary,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 3,
    backgroundColor: colors.brand.primary,
  },
  subtext: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  empty: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: designTokens.spacing.xs,
    paddingVertical: designTokens.spacing.lg,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    textAlign: 'center',
  },
  viewAll: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    marginTop: designTokens.spacing.sm,
    paddingTop: designTokens.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  viewAllText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.brand.primary,
  },
});
